import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { GoogleMap, LoadScript, Marker } from '@react-google-maps/api';
import { Capacitor } from '@capacitor/core';
import Button from './ui/Button';
import Icon from './AppIcon';
import { extractCityDistrict } from '../utils/addressParsing';
import { calculateDistance } from '../services/locationService';
import { useUserLocation } from '../contexts/UserLocationContext';
import {
  getCurrentPositionFast,
  getCurrentPositionAccurate,
  getGeolocationErrorMessage,
  isGeolocationSupported,
} from '../utils/location';

const DEFAULT_CENTER = { lat: 24.7136, lng: 46.6753 };
const MAP_REFINE_MIN_KM = 0.5;

const libraries = ['places'];

const mapContainerStyle = {
  width: '100%',
  height: '400px',
  borderRadius: '0.75rem',
};

function toCoords(loc) {
  if (loc?.lat == null || loc?.lng == null) return null;
  const lat = Number(loc.lat);
  const lng = Number(loc.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
}

const LocationPickerMap = ({
  initialCenter = DEFAULT_CENTER,
  onLocationSelect,
  onCancel,
}) => {
  const { userLocation: contextLocation } = useUserLocation();

  const effectiveSeed = useMemo(() => {
    const fromParent = toCoords(initialCenter);
    const fromContext = toCoords(contextLocation);
    if (!fromParent && !fromContext) return DEFAULT_CENTER;
    if (!fromContext) return fromParent;
    if (!fromParent) return fromContext;
    const distKm = calculateDistance(fromParent.lat, fromParent.lng, fromContext.lat, fromContext.lng);
    if (distKm > 5) return fromContext;
    return fromParent;
  }, [initialCenter, contextLocation]);

  const [selectedLocation, setSelectedLocation] = useState(effectiveSeed);
  const mapRef = useRef(null);
  const [geocoder, setGeocoder] = useState(null);
  const [address, setAddress] = useState('');
  const [loadingAddress, setLoadingAddress] = useState(false);
  const [addressParts, setAddressParts] = useState({ city: '', district: '' });
  const [mapLoaded, setMapLoaded] = useState(false);
  const markerRef = useRef(null);
  const autocompleteInputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loadError, setLoadError] = useState(null);
  const [locatingCurrent, setLocatingCurrent] = useState(false);
  const effectiveSeedRef = useRef(effectiveSeed);
  const onMapLoadGpsStartedRef = useRef(false);

  const googleMapsApiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

  useEffect(() => {
    effectiveSeedRef.current = effectiveSeed;
  }, [effectiveSeed]);

  // SAFE: Check secure context for geolocation (required in most browsers). Native app = always secure.
  const isSecureContext = useCallback(() => {
    if (typeof window === 'undefined') return false;
    if (Capacitor.isNativePlatform()) return true;
    const hostname = window.location.hostname;
    const isLocal = hostname === 'localhost' || hostname === '127.0.0.1';
    const isLocalNetwork = /^(192\.168\.|10\.|172\.(1[6-9]|2[0-9]|3[0-1])\.)/.test(hostname);
    return window.isSecureContext || window.location?.protocol === 'https:' || isLocal || isLocalNetwork;
  }, []);

  // Reverse geocode using Google Maps Geocoder
  // Returns full geocoderResult (results[0]) for consistent parsing
  const reverseGeocode = useCallback(async (location) => {
    if (!window.google || !window.google.maps) {
      setAddress('موقع محدد على الخريطة');
      setAddressParts({ city: '', district: '' });
      return null;
    }

    setLoadingAddress(true);
    try {
      const geocoderInstance = geocoder || new window.google.maps.Geocoder();
      if (!geocoder) {
        setGeocoder(geocoderInstance);
      }

      return new Promise((resolve) => {
        geocoderInstance.geocode(
          { location: location },
          (results, status) => {
            if (status === 'OK' && results && results[0]) {
              const geocoderResult = results[0];
              
              // Extract city and district using unified helper
              const { city, district } = extractCityDistrict(geocoderResult);
              
              console.log('📍 LocationPickerMap geocode result:', {
                geocoderResult,
                extractedCity: city,
                extractedDistrict: district,
                formatted_address: geocoderResult.formatted_address
              });
              
              // Use extracted city/district with fallbacks
              const finalCity = city || geocoderResult.formatted_address || '';
              const finalDistrict = district || '';
              
              // Build display address: "الموقع: {city} — {district}" or just city if no district
              const displayAddress = finalDistrict 
                ? `الموقع: ${finalCity} — ${finalDistrict}`
                : `الموقع: ${finalCity}`;
              
              setAddress(displayAddress);
              setAddressParts({ city: finalCity, district: finalDistrict });
              setLoadingAddress(false);
              resolve(geocoderResult);
            } else {
              setAddress(`موقع محدد: ${location.lat.toFixed(6)}, ${location.lng.toFixed(6)}`);
              setAddressParts({ city: '', district: '' });
              setLoadingAddress(false);
              resolve(null);
            }
          }
        );
      });
    } catch (error) {
      console.error('Geocoding error:', error);
      setAddress(`موقع محدد: ${location.lat.toFixed(6)}, ${location.lng.toFixed(6)}`);
      setAddressParts({ city: '', district: '' });
      setLoadingAddress(false);
      return null;
    }
  }, [geocoder]);

  const applyMapLocation = useCallback((map, location) => {
    const coords = toCoords(location);
    if (!coords) return;
    setSelectedLocation(coords);
    if (map) {
      const latLng = new window.google.maps.LatLng(coords.lat, coords.lng);
      map.panTo(latLng);
      map.setZoom(15);
    }
    reverseGeocode(coords);
  }, [reverseGeocode]);

  // Initialize geocoder when Google Maps is loaded
  useEffect(() => {
    if (window.google && window.google.maps && !geocoder) {
      setGeocoder(new window.google.maps.Geocoder());
      handleLoadSuccess();
    }
    
    // Check for Google Maps loading errors after a delay
    const errorTimer = setTimeout(() => {
      if (!window.google || !window.google.maps) {
        handleLoadError(new Error('Google Maps failed to load. Please check your API key and ensure Maps JavaScript API and Places API are enabled.'));
      }
    }, 10000); // Wait 10 seconds before showing error
    
    return () => clearTimeout(errorTimer);
  }, [geocoder]);

  // Initialize Autocomplete when map and input are ready
  useEffect(() => {
    // Wait for Google Maps API and Places library to be fully loaded
    const initAutocomplete = () => {
      if (
        window.google && 
        window.google.maps && 
        window.google.maps.places && 
        window.google.maps.places.Autocomplete &&
        autocompleteInputRef.current && 
        !autocompleteRef.current
      ) {
        console.log('🔍 Initializing Places Autocomplete...');
        console.log('📍 Input element:', autocompleteInputRef.current);
        console.log('📍 Places API available:', !!window.google.maps.places);
        
        try {
          const autocomplete = new window.google.maps.places.Autocomplete(
            autocompleteInputRef.current,
            {
              // Remove types restriction to allow all results (addresses, businesses, POIs, etc.)
              // types: ['geocode'] limits results too much
              componentRestrictions: { country: 'sa' }, // Restrict to Saudi Arabia only
              fields: ['geometry', 'formatted_address', 'address_components', 'name', 'place_id', 'types'],
              language: 'ar',
            }
          );

          autocomplete.addListener('place_changed', () => {
            const place = autocomplete.getPlace();
            
            console.log('📍 Place selected:', place);
            
            if (!place.geometry || !place.geometry.location) {
              console.warn('No geometry found for selected place');
              return;
            }

            const location = {
              lat: place.geometry.location.lat(),
              lng: place.geometry.location.lng(),
            };

            setSelectedLocation(location);
            setSearchQuery(place.formatted_address || place.name || '');

            // Center map on selected place
            if (mapRef.current) {
              const latLng = new window.google.maps.LatLng(location.lat, location.lng);
              mapRef.current.panTo(latLng);
              
              // Adjust zoom based on place type
              if (place.geometry.viewport) {
                mapRef.current.fitBounds(place.geometry.viewport);
              } else {
                mapRef.current.setZoom(16);
              }
            }

            // Reverse geocode to get city and district
            reverseGeocode(location);
          });

          autocompleteRef.current = autocomplete;
          console.log('✅ Places Autocomplete initialized successfully');
        } catch (error) {
          console.error('❌ Error initializing Autocomplete:', error);
          console.error('Make sure Places API is enabled in Google Cloud Console');
          console.error('Error details:', {
            message: error.message,
            stack: error.stack,
            googleMapsAvailable: !!window.google?.maps,
            placesAvailable: !!window.google?.maps?.places,
            autocompleteAvailable: !!window.google?.maps?.places?.Autocomplete
          });
        }
      } else {
        // Debug why autocomplete is not initializing
        if (!window.google) {
          console.log('⏳ Waiting for Google Maps API...');
        } else if (!window.google.maps) {
          console.log('⏳ Waiting for Google Maps...');
        } else if (!window.google.maps.places) {
          console.log('⏳ Waiting for Places library...');
        } else if (!autocompleteInputRef.current) {
          console.log('⏳ Waiting for input element...');
        } else if (autocompleteRef.current) {
          console.log('✅ Autocomplete already initialized');
        }
      }
    };

    // Try to initialize immediately if ready
    if (mapLoaded) {
      initAutocomplete();
    }

    // Also try after a short delay to ensure everything is loaded
    const timer = setTimeout(() => {
      if (mapLoaded) {
        initAutocomplete();
      }
    }, 1000);

    return () => {
      clearTimeout(timer);
      if (autocompleteRef.current) {
        try {
          window.google?.maps?.event?.clearInstanceListeners(autocompleteRef.current);
        } catch (e) {
          console.warn('Error clearing autocomplete listeners:', e);
        }
        autocompleteRef.current = null;
      }
    };
  }, [mapLoaded, reverseGeocode]);

  // Sync marker when parent seed or context updates
  useEffect(() => {
    setSelectedLocation(effectiveSeed);
  }, [effectiveSeed.lat, effectiveSeed.lng]);

  // Re-center map when selectedLocation changes (after map is loaded)
  useEffect(() => {
    if (mapLoaded && mapRef.current && selectedLocation && selectedLocation.lat && selectedLocation.lng) {
      const newLatLng = new window.google.maps.LatLng(selectedLocation.lat, selectedLocation.lng);
      mapRef.current.panTo(newLatLng);
      mapRef.current.setZoom(15);
    }
  }, [selectedLocation, mapLoaded]);

  // Reverse geocode initial location when map and geocoder are ready
  useEffect(() => {
    if (selectedLocation && window.google && window.google.maps && geocoder) {
      // Small delay to ensure map is fully loaded
      const timer = setTimeout(() => {
        reverseGeocode(selectedLocation);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [selectedLocation, geocoder, reverseGeocode]);

  const refinePinFromGps = useCallback((map, baseCoords) => {
    if (!isGeolocationSupported() || !isSecureContext()) return;

    getCurrentPositionFast()
      .then((position) => {
        const userLoc = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
        const base = baseCoords || effectiveSeedRef.current;
        if (base?.lat != null && base?.lng != null) {
          const distKm = calculateDistance(base.lat, base.lng, userLoc.lat, userLoc.lng);
          if (distKm < MAP_REFINE_MIN_KM) return;
        }
        applyMapLocation(map, userLoc);
      })
      .catch(() => {});
  }, [applyMapLocation, isSecureContext]);

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
    setMapLoaded(true);

    const seed = effectiveSeedRef.current;
    applyMapLocation(map, seed);

    if (onMapLoadGpsStartedRef.current) return;
    onMapLoadGpsStartedRef.current = true;
    refinePinFromGps(map, seed);
  }, [applyMapLocation, refinePinFromGps]);

  useEffect(() => {
    onMapLoadGpsStartedRef.current = false;
  }, [effectiveSeed.lat, effectiveSeed.lng]);

  const onMapUnmount = useCallback(() => {
    mapRef.current = null;
    setMapLoaded(false);
  }, []);

  const handleMapClick = useCallback((event) => {
    const newLocation = {
      lat: event.latLng.lat(),
      lng: event.latLng.lng(),
    };
    setSelectedLocation(newLocation);
    
    // Immediately center map on clicked location
    if (mapRef.current) {
      const latLng = new window.google.maps.LatLng(newLocation.lat, newLocation.lng);
      mapRef.current.panTo(latLng);
      mapRef.current.setZoom(16);
    }
    
    reverseGeocode(newLocation);
  }, [reverseGeocode]);


  const handleConfirm = async () => {
    if (!onLocationSelect) return;
    
    // SAFE MODE: Always reverse geocode to ensure we have fresh, accurate data
    // This prevents issues where addressParts might not be updated yet
    console.log('📍 handleConfirm: Starting reverse geocode for selected location:', selectedLocation);
    
    let finalCity = addressParts.city || '';
    let finalDistrict = addressParts.district || '';
    let finalAddress = address || `موقع محدد: ${selectedLocation.lat.toFixed(6)}, ${selectedLocation.lng.toFixed(6)}`;
    
    // Always reverse geocode to get fresh data (even if we have some data)
    // This ensures city and district are always accurate
    try {
      const geocoderResult = await reverseGeocode(selectedLocation);
      if (geocoderResult) {
        const extracted = extractCityDistrict(geocoderResult);
        finalCity = extracted.city || finalCity || geocoderResult.formatted_address || '';
        finalDistrict = extracted.district || finalDistrict || '';
        
        // Build display address with city and district
        finalAddress = finalDistrict 
          ? `الموقع: ${finalCity} — ${finalDistrict}`
          : `الموقع: ${finalCity}`;
        
        console.log('📍 handleConfirm: Got geocoding result:', {
          finalCity,
          finalDistrict,
          finalAddress
        });
      } else {
        // If geocoding fails, use existing address or coordinates
        console.warn('📍 handleConfirm: Geocoding returned no result, using existing data');
      }
    } catch (error) {
      console.error('📍 handleConfirm: Geocoding error:', error);
      // Continue with existing data if geocoding fails
    }
    
    // Ensure we have at least coordinates-based address if everything else fails
    if (!finalAddress || finalAddress.includes('موقع محدد:')) {
      finalAddress = `موقع محدد: ${selectedLocation.lat.toFixed(6)}, ${selectedLocation.lng.toFixed(6)}`;
    }
    
    console.log('📍 handleConfirm: Calling onLocationSelect with:', {
      lat: selectedLocation.lat,
      lng: selectedLocation.lng,
      address: finalAddress,
      city: finalCity,
      district: finalDistrict
    });
    
    // Call onLocationSelect with all data
    onLocationSelect({
      lat: selectedLocation.lat,
      lng: selectedLocation.lng,
      address: finalAddress,
      city: finalCity,
      district: finalDistrict
    });
  };

  const handleLoadError = (error) => {
    console.error('❌ Google Maps LoadScript error:', error);
    setLoadError('فشل تحميل Google Maps. يرجى التحقق من API Key و Places API.');
  };

  const handleLoadSuccess = () => {
    console.log('✅ Google Maps loaded successfully');
    setLoadError(null);
  };

  if (!googleMapsApiKey) {
    return (
      <div className="bg-slate-100 rounded-lg p-8 h-96 flex items-center justify-center border-2 border-dashed border-slate-300">
        <div className="text-center">
          <Icon name="AlertCircle" size={48} className="mx-auto mb-4 text-amber-500" />
          <p className="text-sm text-slate-600 mb-2">مفتاح Google Maps غير موجود</p>
          <p className="text-xs text-slate-500">
            يرجى إضافة VITE_GOOGLE_MAPS_API_KEY في ملف .env
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" dir="ltr">
      {loadError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
          <div className="flex items-center gap-2 text-red-700">
            <Icon name="AlertCircle" size={20} />
            <div>
              <p className="font-medium">{loadError}</p>
              <p className="text-sm mt-1">
                تأكد من تفعيل Maps JavaScript API 
              </p>
            </div>
          </div>
        </div>
      )}
      <LoadScript
        googleMapsApiKey={googleMapsApiKey}
        libraries={libraries}
        language="ar"
        region="SA"
        loadingElement={<div className="h-96 flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>}
      >
        <div className="relative">
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={selectedLocation}
            zoom={15}
            onLoad={onMapLoad}
            onUnmount={onMapUnmount}
            onClick={handleMapClick}
            options={{
              disableDefaultUI: false,
              zoomControl: true,
              streetViewControl: false,
              mapTypeControl: false,
              fullscreenControl: true,
              clickableIcons: true, // Enable to show business/place info
              gestureHandling: 'greedy',
            }}
          >
            <Marker
              position={selectedLocation}
              draggable={true}
              onDragEnd={(event) => {
                const newLocation = {
                  lat: event.latLng.lat(),
                  lng: event.latLng.lng(),
                };
                setSelectedLocation(newLocation);
                
                // Center map on dragged location
                if (mapRef.current) {
                  const latLng = new window.google.maps.LatLng(newLocation.lat, newLocation.lng);
                  mapRef.current.panTo(latLng);
                  mapRef.current.setZoom(16);
                }
                
                reverseGeocode(newLocation);
              }}
              animation={window.google?.maps?.Animation ? window.google.maps.Animation.DROP : undefined}
            />
          </GoogleMap>
          
          {/* Search Box Overlay */}
          <div className="absolute top-4 left-4 right-4 z-10">
            <div className="bg-white rounded-lg shadow-lg border border-slate-200 p-2 flex items-center gap-2">
              <Icon name="Search" size={20} className="text-slate-400 flex-shrink-0" />
              <input
                ref={autocompleteInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  // Clear autocomplete value if user manually types
                  if (autocompleteInputRef.current && autocompleteRef.current) {
                    // Allow user to type and see autocomplete suggestions
                  }
                }}
                placeholder="ابحث عن مكان، نشاط، شركة، أو عنوان..."
                className="flex-1 outline-none text-sm text-slate-700 placeholder:text-slate-400"
                dir="rtl"
                autoComplete="off"
              />
              {searchQuery && (
                <button
                  onClick={() => {
                    setSearchQuery('');
                    if (autocompleteInputRef.current) {
                      autocompleteInputRef.current.value = '';
                    }
                    // Reset autocomplete
                    if (autocompleteRef.current) {
                      autocompleteRef.current.set('place', null);
                    }
                  }}
                  className="p-1 hover:bg-slate-100 rounded transition-colors"
                  aria-label="مسح البحث"
                >
                  <Icon name="X" size={16} className="text-slate-400" />
                </button>
              )}
            </div>
            {!window.google?.maps?.places && mapLoaded && (
              <div className="mt-2 bg-amber-50 border border-amber-200 rounded-lg p-2 text-xs text-amber-700">
                <p>⚠️ Places API غير مفعل. يرجى تفعيله في Google Cloud Console.</p>
              </div>
            )}
          </div>
          
          {/* Current Location Button - SAFE: secure context + timeout so it always responds */}
          <button
            type="button"
            onClick={async () => {
              if (!isGeolocationSupported()) {
                alert('المتصفح لا يدعم تحديد الموقع الجغرافي. يرجى تحديد الموقع يدوياً على الخريطة.');
                return;
              }
              if (!isSecureContext()) {
                alert('تحديد الموقع يتطلب اتصالاً آمناً (https). يرجى تحديد الموقع يدوياً على الخريطة أو البحث عن مكان.');
                return;
              }
              setLocatingCurrent(true);
              try {
                const fastPos = await getCurrentPositionFast();
                const fastLoc = {
                  lat: fastPos.coords.latitude,
                  lng: fastPos.coords.longitude,
                };
                applyMapLocation(mapRef.current, fastLoc);

                getCurrentPositionAccurate()
                  .then((accuratePos) => {
                    applyMapLocation(mapRef.current, {
                      lat: accuratePos.coords.latitude,
                      lng: accuratePos.coords.longitude,
                    });
                  })
                  .catch(() => {});
              } catch (error) {
                alert(getGeolocationErrorMessage(error));
              } finally {
                setLocatingCurrent(false);
              }
            }}
            disabled={locatingCurrent}
            className="absolute bottom-10 left-4 bg-white hover:bg-slate-50 rounded-lg px-4 py-2 shadow-lg border border-slate-200 z-10 transition-all hover:shadow-xl flex items-center gap-2 text-sm font-medium text-slate-700 disabled:opacity-70 disabled:pointer-events-none"
            title="تحديد موقعي الحالي"
            aria-label="تحديد موقعي الحالي"
          >
            {locatingCurrent ? (
              <span className="inline-block w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            ) : (
              <Icon name="Navigation" size={18} className="text-primary" />
            )}
            <span>موقعي الحالي</span>
          </button>
          
          {/* Instructions Overlay — hidden */}
        </div>

        {/* Address Display */}
        <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="MapPin" size={16} className="text-primary" />
            <span className="text-sm font-medium text-slate-700">الموقع المحدد:</span>
          </div>
          {loadingAddress ? (
            <div className="flex items-center gap-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
              <span className="text-sm text-slate-600">جاري جلب العنوان...</span>
            </div>
          ) : (
            <p className="text-sm text-slate-800 font-medium">{address || 'لم يتم تحديد موقع'}</p>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <Button
            variant="ghost"
            onClick={onCancel}
            className="flex-shrink-0"
          >
            إلغاء
          </Button>
          <Button
            onClick={handleConfirm}
            iconName="Check"
            iconPosition="right"
            className="flex-1 shadow-md hover:shadow-lg transition-shadow"
            disabled={!selectedLocation}
          >
            حفظ الموقع
          </Button>
        </div>
      </LoadScript>
    </div>
  );
};

export default LocationPickerMap;
